import { useRef, useState, useCallback, useEffect } from "react";
import { Box, Typography, IconButton, Chip } from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";

export interface ProcessedImage {
    id: string;
    fileName: string;
    resizedFull: Blob;
    resizedThumb?: Blob;
    preview: string;
}

interface GenericImageSelectorProps {
    onChangeImages: (images: ProcessedImage[]) => void;

    imageFieldName?: string;
    thumbFieldName?: string;

    fullWidth?: number;
    fullHeight?: number;
    thumbWidth?: number;
    thumbHeight?: number;
    dropzoneText?: string;
}

const resizeImage = (file: File, width: number, height: number): Promise<Blob> =>
    new Promise((resolve, reject) => {
        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
            URL.revokeObjectURL(url);
            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext("2d")!;

            const scale = Math.max(width / img.width, height / img.height);
            const sw = width / scale;
            const sh = height / scale;
            const sx = (img.width - sw) / 2;
            const sy = (img.height - sh) / 2;
            ctx.drawImage(img, sx, sy, sw, sh, 0, 0, width, height);

            canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("Canvas falhou"))), "image/jpeg", 0.9);
        };
        img.onerror = () => reject(new Error("Erro ao carregar imagem"));
        img.src = url;
    });

export const GenericImageSelector = ({
                                         onChangeImages,
                                         thumbFieldName,
                                         fullWidth = 800,
                                         fullHeight = 600,
                                         thumbWidth = 320,
                                         thumbHeight = 240,
                                         dropzoneText,
                                     }: GenericImageSelectorProps) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [files, setFiles] = useState<ProcessedImage[]>([]);
    const [isDragging, setIsDragging] = useState(false);

    // Notifica o componente pai sempre que o estado local mudar
    useEffect(() => {
        onChangeImages(files);
    }, [files, onChangeImages]);

    const addFiles = useCallback(async (incoming: FileList | null) => {
        if (!incoming) return;
        const imageFiles = Array.from(incoming).filter((f) => f.type.startsWith("image/"));

        const processed = await Promise.all(
            imageFiles.map(async (file, index) => {
                const resizedFull = await resizeImage(file, fullWidth, fullHeight);

                let resizedThumb: Blob | undefined = undefined;
                if (thumbFieldName) {
                    resizedThumb = await resizeImage(file, thumbWidth, thumbHeight);
                }

                const previewUrl = resizedThumb ? URL.createObjectURL(resizedThumb) : URL.createObjectURL(resizedFull);

                return {
                    id: `${file.name}-${Date.now()}-${Math.random()}-${index}`,
                    fileName: file.name,
                    resizedFull,
                    resizedThumb,
                    preview: previewUrl,
                };
            })
        );

        setFiles((prev) => [...prev, ...processed]);
    }, [fullWidth, fullHeight, thumbWidth, thumbHeight, thumbFieldName]);

    const removeFile = (id: string) => {
        setFiles((prev) => {
            const removed = prev.find((f) => f.id === id);
            if (removed) URL.revokeObjectURL(removed.preview);
            return prev.filter((f) => f.id !== id);
        });
    };

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box
                onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(e) => { e.preventDefault(); setIsDragging(false); addFiles(e.dataTransfer.files); }}
                onClick={() => inputRef.current?.click()}
                sx={{
                    border: "2px dashed",
                    borderColor: isDragging ? "primary.main" : "divider",
                    borderRadius: 2,
                    p: 4,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    bgcolor: isDragging ? "action.hover" : "background.paper",
                    transition: "all 0.2s ease",
                    minHeight: 120,
                    "&:hover": { borderColor: "primary.main", bgcolor: "action.hover" },
                }}
            >
                <CloudUploadIcon sx={{ fontSize: 36, color: "text.secondary" }} />
                <Typography variant="body1" fontWeight={500}>
                    {dropzoneText || "Arraste imagens aqui ou clique para selecionar"}
                </Typography>
                <input ref={inputRef} type="file" accept="image/*" multiple hidden onChange={(e) => addFiles(e.target.files)} />
            </Box>

            {files.length > 0 && (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {files.map((f) => (
                        <Box key={f.id} sx={{ position: "relative", width: 100, height: 75, borderRadius: 1, overflow: "hidden", border: "1px solid", borderColor: "divider" }}>
                            <Box component="img" src={f.preview} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            <IconButton
                                size="small"
                                onClick={(e) => { e.stopPropagation(); removeFile(f.id); }}
                                sx={{ position: "absolute", top: 2, right: 2, bgcolor: "rgba(0,0,0,0.6)", color: "white", p: "2px", "&:hover": { bgcolor: "error.main" } }}
                            >
                                <DeleteIcon sx={{ fontSize: 12 }} />
                            </IconButton>
                        </Box>
                    ))}
                </Box>
            )}
        </Box>
    );
};